import { where, writeBatch } from 'firebase/firestore';
import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { convertHelpers } from 'global/common/convert';
import { createRefPath, createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { getSportsYear } from 'global/firestoreData/helpers/years';
import { useCallback, useContext, useMemo, useState } from 'react'
import { seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { seasonalHelpers } from '../cnr/reducers/reducerHelpers/seasonalHelpers';
import { logBatch } from 'global/cnr/reducers/reducerHelpers/logging';
import { _allowSportsUpdates } from './useSeasonals';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';

const _pksScores = ['organizations', 'districts', 'sports', 'sportsYears']

export const useSeasonalDataHelpers = () => {

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states
  const { database_fns } = fns
  const { pathViews } = paps_state ?? {}
  const { mainDb } = app_state ?? {}

  const [updating, setUpdating] = useState()

  const _backupSeasonal = useCallback((dataParents, altSeasonalName, dataRestrictions, dispatch) =>
    backupSeasonal(
      database_fns,
      pathViews,
      dataParents,
      altSeasonalName,
      dataRestrictions,
      dispatch
    ),
    [database_fns, pathViews]
  )

  const _createSeasonInit = useCallback(
    (sportsKey, callback) =>
      getData_createSeasonInit(
        database_fns,
        pathViews,
        sportsKey,
        callback
      ),
    [database_fns, pathViews]
  )

  const _deleteSeasonalData = useCallback(
    (
      dataParents,
      dataType,
      data,
      callback,
      altData,
      altSeasonalName
    ) =>
      deleteSeasonalData(
        database_fns,
        pathViews,
        dataParents,
        dataType,
        data,
        callback,
        altData,
        altSeasonalName
      ),
    [database_fns, pathViews]
  )

  const _getSeasonalData = useCallback(
    (
      sportsKey,
      latestSeason,
      callback,
      sportsSeason_handlers,
      opts
    ) =>
      getSeasonalData(
        database_fns,
        pathViews,
        sportsKey,
        latestSeason,
        callback,
        sportsSeason_handlers,
        opts
      ),
    [database_fns, pathViews]
  )

  const _getSports_collectionItem = useCallback(
    (
      parentKeys,
      collectionName,
      returnFirstObject,
      cbProps,
      callback,
      collectionKey
    ) =>
      getSports_collectionItem(
        database_fns,
        pathViews,
        parentKeys,
        collectionName,
        returnFirstObject,
        cbProps,
        callback,
        collectionKey
      ),
    [database_fns, pathViews]
  )

  const _getSportSeasonals = useCallback(
    (callback) =>
      getSportSeasonals(
        database_fns,
        pathViews,
        callback
      ),
    [database_fns, pathViews]
  )

  const _getUpdateProps = useCallback(
    (
      sportPermissions,
      pks,
      callback,
      collectionName,
      documentFieldName,
      data,
      opts,
      dataInfos,
      _sportsYearAbr
    ) =>
      getUpdateProps(
        database_fns,
        sportPermissions,
        pathViews,
        pks,
        callback,
        collectionName,
        documentFieldName,
        data,
        opts,
        dataInfos,
        _sportsYearAbr
      ),
    [database_fns, pathViews]
  )

  const _update_seasonalOrGlobal = useCallback(
    (
      seasonalUpdateType,
      updateProps,
      data_new
    ) =>
      update_seasonalOrGlobal(
        mainDb,
        database_fns,
        pathViews,
        seasonalUpdateType,
        updateProps,
        data_new
      ),
    [mainDb, database_fns, pathViews]
  )

  const _updateSeasonalMatches_allByLevel = useCallback(
    (updateProps) =>
      updateSeasonalMatches_allByLevel(
        database_fns,
        pathViews,
        updateProps
      ),
    [database_fns, pathViews]
  )

  const _updateSeasonalMatches_byLevel = useCallback((updateProps) => {
    const cb = () => {
      setUpdating(false)
      updateProps.callback && updateProps.callback()
    }
    setUpdating(true)
    updateSeasonalMatches_byLevel(
      mainDb,
      database_fns,
      pathViews,
      updateProps,
      cb,
    )
  },
    [mainDb, database_fns, pathViews]
  )

  const _updateSeasonalPlayoffMatchesByLevel = useCallback(
    (
      seasonalUpdateType,
      updateProps,
      collectionName,
      data
    ) =>
      updateSportsCollectionItemsWithParentKeys(
        database_fns,
        seasonalUpdateType,
        updateProps,
        collectionName,
        data
      ),
    [database_fns]
  )

  const _updateSportsCollectionItemsWithParentKeys = useCallback(
    (
      seasonalUpdateType,
      updateProps,
      collectionName,
      data
    ) =>
      updateSportsCollectionItemsWithParentKeys(
        database_fns,
        seasonalUpdateType,
        updateProps,
        collectionName,
        data
      ),
    [database_fns]
  )

  return useMemo(
    () => ({
      backupSeasonal: _backupSeasonal,
      createSeasonInit: _createSeasonInit,
      deleteSeasonalData: _deleteSeasonalData,
      getSeasonalData: _getSeasonalData,
      getSports_collectionItem: _getSports_collectionItem,
      getSportSeasonals: _getSportSeasonals,
      getUpdateProps: _getUpdateProps,
      update_seasonalOrGlobal: _update_seasonalOrGlobal,
      updateSeasonalMatches_allByLevel: _updateSeasonalMatches_allByLevel,
      updateSeasonalMatches_byLevel: _updateSeasonalMatches_byLevel,
      updateSeasonalPlayoffMatchesByLevel: _updateSeasonalPlayoffMatchesByLevel,
      updateSportsCollectionItemsWithParentKeys: _updateSportsCollectionItemsWithParentKeys,
      updating
    }),
    [
      _backupSeasonal,
      _createSeasonInit,
      _deleteSeasonalData,
      _getSeasonalData,
      _getSports_collectionItem,
      _getSportSeasonals,
      _getUpdateProps,
      _update_seasonalOrGlobal,
      _updateSeasonalMatches_allByLevel,
      _updateSeasonalMatches_byLevel,
      _updateSeasonalPlayoffMatchesByLevel,
      _updateSportsCollectionItemsWithParentKeys,
    ]
  )
}


const modifyParentKeys = (pks) => {
  if (!pks) return false;
  _.forEach(pks, (pk, key) => {
    if (!_pksScores.includes(key)) {
      delete pks[key]
    }
  })
}

const checkParentKeys = (pks) => {
  let allow = true
  if (Object.keys(pks).length === _pksScores.length) {
    _pksScores.forEach(pk => {
      if (!pks[pk]) {
        allow = false
      }
    })
    return allow
  } else {
    return false
  }
}

const getUpdateProps = (database_fns, sportPermissions, pathViews, pks, callback, collectionName, documentFieldName, data, opts, dataInfos, _sportsYearAbr) => {
  const updateProps = { database_fns, sportPermissions, pathViews }
  if (pks) { updateProps.pks = pks }
  if (callback) { updateProps.callback = callback }
  if (collectionName) { updateProps.collectionName = collectionName }
  if (documentFieldName) { updateProps.documentFieldName = documentFieldName }
  if (data) { updateProps.data = data }
  if (opts) { updateProps.opts = opts }
  if (dataInfos) { updateProps.dataInfos = dataInfos }
  if (_sportsYearAbr) {
    updateProps._sportsYearAbr = _sportsYearAbr
    updateProps.useAddKey = true
  }
  updateProps.forSeasonal = !updateProps.collectionName ? true : false
  return updateProps
}

// const ammend_updateProps = (updateProps, pks, callback) => {
//   updateProps.pks = pks
//   updateProps.callback = callback
// }

// const ammend_updateNameProps = (updateProps, collectionName, documentFieldName, data, opts) => {
//   if (collectionName) { updateProps.collectionName = collectionName }
//   if (documentFieldName) { updateProps.documentFieldName = documentFieldName }
//   if (data) {
//     updateProps.data = data
//     updateProps.dataToUpdate = data
//   }
//   if (opts) { updateProps.opts = opts }
//   updateProps.forSeasonal = !updateProps.collectionName ? true : false
// }

const getSportSeasonals = (database_fns, pathViews, callback) => {
  const rp = createRefPath_event(pathViews, ['_seasonals'])
  const wheres = []
  wheres.push(where('parentKeys.organizations', '==', pathViews.organizations))
  wheres.push(where('parentKeys.districts', '==', pathViews.districts))
  wheres.push(where('parentKeys.sports', '==', pathViews.sports))
  database_fns.get_data({ refPath: rp, wheres, callback })
}

/**
 * returns data from a collection based on the seasonalCollectionName/altSeasonalName where the parentKey constraints match
 * @param {array} pathViews 
 * @param {array} dataParents 
 * @param {boolean} useSeasonals 
 * @param {function} callback 
 * @param {function} sportsSeason_handlers 
 * @param {string} altSeasonalName 
 * @returns data from a collection based on the seasonalCollectionName/altSeasonalName where the parentKey constraints match
 * pojoSports
 * 
 */
const getSeasonalData = async (database_fns, pathViews, sportsKey, latestSeason, callback, sportsSeason_handlers, opts) => {

  const { altSeasonalName, getSeasonalMatchesOnly } = opts ?? {}

  const collectionName = altSeasonalName ? altSeasonalName : _seasonalCollectionName
  const sy = getSportsYear(latestSeason)

  const pks = seasonalHelpers.getPks(pathViews, sy, sportsKey)

  if (pks) {

    const parentKeys = {}

    const _baseRef = createRefPath_event(pathViews, [collectionName])
    const wheres = []

    Object.keys(pks).forEach(pk => {
      wheres.push(where('parentKeys.' + pk, '==', pks[pk]))
      parentKeys[pk] = pathViews[pk]
    })

    // AWAIT THE DATA 
    const { item: retData, fk } = await database_fns.get_data({ refPath: _baseRef, wheres, opts: { returnFirstObject: true, ignoreId: true, returnKey: true } })

    if (getSeasonalMatchesOnly) {
      const refPathM = createRefPath_event(pathViews, [collectionName, fk, 'matches', 'matches'])
      const { item: retData_n } = await database_fns.get_data({ refPath: refPathM, opts: { returnFirstObject: true, ignoreId: true, returnKey: true } })
      callback(retData_n)
      return
    }

    if (retData && retData.id) {
      const _baseRef2 = createRefPath([retData.id, 'details'], _baseRef)
      const retData2 = await database_fns.get_data({ refPath: _baseRef2, opts: { ignoreId: true } })
      if (callback) {
        callback(retData2, fk)
        return retData2
      } else {
        return retData2
      }
    }

    if (retData && retData._itemKey) {
      const _baseRef2 = createRefPath([retData._itemKey, 'details'], _baseRef)
      const retData2 = await database_fns.get_data({ refPath: _baseRef2 })
      if (callback) {
        callback(retData2, fk)
        return retData2
      } else {
        return retData2
      }
    }

    if (sportsSeason_handlers) {

      const si = retData

      if (si && si.matches && si.parentKeys) {
        addParentKeysToDataItem(si.parentKeys, si.matches)
      }
      if (si && si.teams && si.parentKeys) { addParentKeysToDataItem(si.parentKeys, si.teams) }
      if (altSeasonalName) {
        sportsSeason_handlers.setPlayoffMatches(si)
      } else {
        sportsSeason_handlers.handleSet_sportsSeasonData(si)
      }
    } else {
      if (callback) {
        callback(retData, fk)
      } else {
        return retData
      }
    }
  } else {
    if (callback) {
      callback(null)
    } else {
      return null
    }
  }
}

/**
 * 
 * @param {object} pathViews 
 * @param {object} pks 
 * @param {string} documentFieldName 
 * @param {object} data 
 * @param {string} collectionName ** with _
 * @param {function} callback 
 * @description Updates the `_seasonalCollectionName` or the collectionName prefixed with an `_`
 */
const update_seasonalOrGlobal = async (mainDb, database_fns, pathViews, seasonalUpdateType, updateProps, data_new) => {

  const {
    _seasonalKey,
    _sportsYearAbr,
    callback,
    collectionName,
    data,
    dataInfos,
    documentFieldName,
    forSeasonal,
    opts,
    pks,
    sportPermissions,
    useAddKey,
  } = updateProps

  // ************************************************************************************
  const allowUpdate = _allowSportsUpdates.seasonals && sportPermissions?.any && sportPermissions?.[seasonalUpdateType]

  const { dtu, dta } = dataInfos ? dataInfos : {}

  // _sportsAccess_sports

  const { rtProps, appUserInfo, useLevelSeparation, subCollectionName, subDocumentKey, fullData, ignoreSeason, updateToMain } = opts ?? {}

  if (updateToMain) {
    // pathViews, collectionName, parentKeys, data
    if (allowUpdate) {
      updateSportsCollectionItemsWithParentKeys(database_fns, seasonalUpdateType, updateProps, documentFieldName, dtu ? dtu : data, callback)
    }
  } else {
    if (pks.sportsYears || ignoreSeason) {

      let _collectionName = forSeasonal ? _seasonalCollectionName : '_' + collectionName
      let _subCollectionName;

      switch (_collectionName) {
        case '_matches':
          _subCollectionName = 'matches'
          break;
        case '_playoffMatches':
          _subCollectionName = 'playoffMatches'
          break;
        case '_scores':
          _subCollectionName = 'scores'
          break;
        case '_playoffScores':
          _subCollectionName = 'playoffScores'
          break;
        case '_backup_matches':
          _subCollectionName = 'matches'
          break;
        // case '_rankings':
        //   _subCollectionName = 'rankings'
        //   break;
        default:
        // nothing
      }

      switch (_collectionName) {
        case '_matches':
        case '_playoffMatches':
        case '_scores':
        case '_playoffScores':
          _collectionName = _sportCollections._matches_seasonal
          break;
        default:
        // nothing
      }

      const _timestamp = Date.now()
      const batch = writeBatch(mainDb);

      const _sportsYearCollectionItem = await getSports_collectionItem(database_fns, pathViews, pks, _collectionName, true)

      if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {

        const { _itemKey } = _sportsYearCollectionItem
        const _docItemKey = _seasonalKey ? _seasonalKey : _itemKey

        let _refPath = subCollectionName ? createRefPath_event(pathViews, [_collectionName, _docItemKey, subCollectionName, subCollectionName]) : createRefPath_event(pathViews, [_collectionName, _docItemKey])

        if (_subCollectionName) {
          _refPath = createRefPath_event(pathViews, [_collectionName, _docItemKey, _subCollectionName, subDocumentKey])
        }

        const _data = fullData ? fullData : data

        switch (seasonalUpdateType) {

          case seasonalUpdateTypes.playoffBrackets:
          case seasonalUpdateTypes.playoffSeeds:
          case seasonalUpdateTypes.playoffTeams:

            const _dataToSet = dta ? dta : _data
            const dataToSet = documentFieldName ? { [documentFieldName]: _dataToSet } : _dataToSet
            database_fns.set_doc(_refPath, dataToSet, true, batch ? null : callback, batch)
            break;

          case seasonalUpdateTypes.backupMatches:
          case seasonalUpdateTypes.matchesCreate:
          case seasonalUpdateTypes.matchesList:

            const _matchesToUpdate = dtu ? dtu : _data

            if (useLevelSeparation) {
              await Promise.all(_.map(_matchesToUpdate, async (_levelMatches, levelKey) => {
                convertHelpers.createItemKeys(_levelMatches)
                const _refPathS = createRefPath_event(pathViews, [_collectionName, _itemKey, _subCollectionName, levelKey]);
                return updateLevelMatches(database_fns, _refPathS, documentFieldName, _levelMatches, allowUpdate, false, batch);
              }));
              // callback()
            } else {
              convertHelpers.createItemKeys(_matchesToUpdate)

              const _levelsToUpdate = _.groupBy(_matchesToUpdate, '_matchCollectionKey')

              Object.keys(_levelsToUpdate).forEach(async levelKey => {
                const _levelMatches = _levelsToUpdate[levelKey]
                const _refPathS = createRefPath_event(pathViews, [_collectionName, _itemKey, _subCollectionName, levelKey])
                await updateLevelMatches(database_fns, _refPathS, documentFieldName, _levelMatches, allowUpdate, true, batch)
              })
            }
            break;

          default:
            const _dataToUpdate = dtu ? dtu : _data
            let dataToUpdate = documentFieldName ? { [documentFieldName]: _dataToUpdate } : _dataToUpdate
            // dataToUpdate.parentKeys = { ...pks }
            dataToUpdate = dataFix.removeEmpties(dataToUpdate)
            console.log('Data to UPDATE', _refPath, dataToUpdate)
            console.groupEnd()
            database_fns.update_doc(_refPath, dataToUpdate, batch ? null : callback, batch)
        }

      } else {

        // NEW DATA
        const _refPath = createRefPath_event(pathViews, [_collectionName])

        let _data = dta ? dta : data

        // set_doc: (refPath, data, merge, dispatch, batch, altDb)

        switch (_collectionName) {
          case '_matches_seasonal':
          case '_backup_matches':
            // add to the main doc 
            const data_pks = { parentKeys: pks }
            console.log('dataToAdd_m', data_pks, _data)
            console.groupEnd()
            if (_seasonalKey) {
              // create a new document in the _collectionName using the _seasonalKey
              const _refPath_sk = createRefPath_event(pathViews, [_collectionName, _seasonalKey])
              const res = await database_fns.set_doc(_refPath_sk, data_pks)
              console.log('res', res)
              console.groupEnd()
              await createMatchesNew(database_fns, allowUpdate, pathViews, documentFieldName, _data, _collectionName, _seasonalKey, batch)
              callback && callback()
            } else if (_sportsYearAbr) {
              // create a new document in the _collectionName
              const _newDocKey = _sportsYearAbr + '_' + database_fns.get_docKey()
              const _refPath_sk = createRefPath_event(pathViews, [_collectionName, _newDocKey])
              // create a new doc with the data_pks being the only data 
              await database_fns.set_doc(_refPath_sk, data_pks)
              // now add the new matches collection to that document
              await createMatchesNew(database_fns, allowUpdate, pathViews, documentFieldName, _data, _collectionName, _newDocKey, batch)
              callback && callback()
            } else {
              // add a new document in the _collectionName 
              // refPath, data, dispatch, batch, appUserAccess, storagePaths, vit
              const res = await database_fns.add_doc(_refPath, data_pks)
              if (res && res.id) {
                // now add the new matches collection to that new document
                await createMatchesNew(database_fns, allowUpdate, pathViews, documentFieldName, _data, _collectionName, res.id, batch)
                callback && callback()
              }
            }
            break;

          default:
            delete _data._itemKey
            const dataToAdd = getDataToAdd(pks, collectionName, documentFieldName, _data, fullData, data_new)

            if (_seasonalKey) {
              const _refPath_sk = createRefPath_event(pathViews, [_collectionName, _seasonalKey])
              console.log('Data to SET', _refPath_sk, dataToAdd)
              console.groupEnd()
              database_fns.set_doc(_refPath_sk, dataToAdd, false, batch ? null : callback, batch)
            } else if (useAddKey && _sportsYearAbr) {
              const _newDocKey = _sportsYearAbr + '_' + database_fns.get_docKey()
              const _refPath_sk = createRefPath_event(pathViews, [_collectionName, _newDocKey])
              console.log('Data to ADD', _refPath_sk, dataToAdd)
              console.groupEnd()
              database_fns.set_doc(_refPath_sk, dataToAdd, false, batch ? null : callback, batch)
            } else {
              console.log('Data to ADD', _refPath, dataToAdd)
              console.groupEnd()
              // refPath, data, dispatch, batch, appUserAccess, storagePaths, vit
              database_fns.add_doc(_refPath, dataToAdd, batch ? null : callback, batch)
            }

        }
      }

      logBatch(batch)

      if (allowUpdate) {
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonMatchesInfo, pathViews.sports, _timestamp, batch)
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
        batch.commit()
        callback()
      } else {
        callback()
      }
    }
  }
}

const updateSeasonalMatches_allByLevel = async (database_fns, pathViews, updateProps) => {
  const { matchDataUpdates, forPlayoffs, repairResults } = updateProps
  const { season, seasonPlayoff } = matchDataUpdates
  if (repairResults) {
    updateProps.dataToUpdate = repairResults
  } else {
    updateProps.dataToUpdate = forPlayoffs ? seasonPlayoff : season
  }
  await updateSeasonalMatches_byLevel(database_fns, pathViews, updateProps)
}

/**
 * dataToUpdate is NOT seperated by level. Keep the levels intact
 * @param {object} updateProps (callback, dataToUpdate, sportPermissions)
 */
const updateSeasonalMatches_byLevel = async (mainDb, database_fns, pathViews, updateProps, callback) => {

  const {
    dataToUpdate,
    sportPermissions,
    pks,
    collectionName,
  } = updateProps

  const dataLength = Object.keys(dataToUpdate).length

  const allowUpdate = _allowSportsUpdates.matches && sportPermissions?.any

  const _dtus = []

  const cb = (res) => {
    if (_dtus.length === dataLength) {
      callback && callback({ done: true })
    } else {
      callback && callback({ done: true })
    }
  }

  const _collectionName = collectionName ? '_' + collectionName : _seasonalCollectionName
  const _sportsYearCollectionItem = await getSports_collectionItem(database_fns, pathViews, pks, _collectionName, true)

  const _timestamp = Date.now()
  const batch = writeBatch(mainDb);

  await Promise.all(
    Object.keys(dataToUpdate).map(levelKey => {
      const _dataToUpdate = dataToUpdate[levelKey];
      const seasonalProps = { ...updateProps, levelKey, dataToUpdate: _dataToUpdate, sportPermissions, database_fns };
      return updateSeasonalMatches
        (
          database_fns,
          pathViews,
          seasonalProps,
          _sportsYearCollectionItem,
          batch,
          _dtus
        );
    })
  );

  logBatch(batch, dataToUpdate)
  console.log('_dtus', _dtus)

  if (allowUpdate) {
    // tsType, tsKey, ts, batch, altDb
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonMatchesInfo, pathViews.sports, _timestamp, batch)
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
    batch.commit()
    cb()
  } else {
    callback({ done: true })
  }

}

const updateSeasonalMatches = async (database_fns, pathViews, seasonalProps, _sportsYearCollectionItem, batch, _dtus) => {

  const {
    callback,
    collectionName,
    dataToUpdate,
    filterType,
    forPlayoffs,
    forRefresh,
    levelKey,
    matchesToUpdate,
    pks,
    playoffMatches,
    replaceData,
    seasonalUpdateType,
    sportPermissions,
    useMatchField,
    useScoringCollection,
  } = seasonalProps

  let _seasonalUpdateType = seasonalUpdateType

  switch (_seasonalUpdateType) {
    case seasonalUpdateTypes.playoffBrackets_all:
      _seasonalUpdateType = seasonalUpdateTypes.playoffBrackets
      break;
    default:
    // nothing
  }

  const _subCollection = forPlayoffs || levelKey === 'playoffs' || filterType === 'seasonPlayoff' ? 'playoffMatches' : 'matches'
  const _subCollection_results = forPlayoffs ? 'playoffResults' : 'results'

  if (pks.sportsYears) {

    const _collectionName = collectionName ? '_' + collectionName : _seasonalCollectionName

    modifyParentKeys(pks)
    const allowPks = checkParentKeys(pks)

    // check to see the the parentKeys have the needed information to proceed.
    if (allowPks) {

      // get the collection item from the database.
      // const _sportsYearCollectionItem = await getSports_collectionItem(database_fns, pathViews, pks, _collectionName, true)

      if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {

        const { _itemKey } = _sportsYearCollectionItem

        // create the refPath
        const _refPath = createRefPath_event(pathViews, [_collectionName, _itemKey, _subCollection, levelKey])

        seasonalHelpers.cleanMatchesForUpdate(dataToUpdate)
        // const _dtu = filterValidMatches(dataToUpdate, _requiredMatchProps)

        const _cb_local = (opts) => {
          callback && callback(opts)
          // if (useScoringCollection) {
          //   const _refPath_scores = createRefPath_event(pathViews, [_collectionName, _itemKey, _subCollection_scores, levelKey])
          //   const _scores = sportsHelpers.getMatchesScores(dataToUpdate, forPlayoffs ? 'playoffScores' : 'scores')
          //   // console.log('_refPath_scores', _refPath_scores)
          //   // console.log('_scores', _scores)
          //   if (allowUpdate) {
          //     database_fns.update_doc(_refPath_scores, _scores, batch ? null : callback, batch)
          //   } else {
          //     callback && callback(opts)
          //   }
          // } else {
          //   callback && callback(opts)
          // }
        }

        if (replaceData) {
          const _dataToUpdate = { playoffMatches: dataToUpdate }
          _dtus.push({ _refPath, _dataToUpdate })
          database_fns.set_doc(_refPath, _dataToUpdate, false, batch ? null : _cb_local, batch)
        } else {
          const result = await database_fns.get_doc({ refPath: _refPath })
          if (_.isEmpty(result) || useMatchField) {
            const m = createMatchesFromDot(dataToUpdate, _subCollection)
            const _dataToUpdate = { [_subCollection]: m }
            _dtus.push({ _refPath, _dataToUpdate })
            database_fns.set_doc(_refPath, _dataToUpdate, false, batch ? null : _cb_local, batch)
          } else {
            if (forRefresh) {
              const m = createMatchesFromDot(dataToUpdate, _subCollection)
              const _dataToUpdate = { [_subCollection]: m }
              _dtus.push({ _refPath, _dataToUpdate })
              database_fns.set_doc(_refPath, _dataToUpdate, false, batch ? null : _cb_local, batch)
            } else {
              // refPath, data, callback, batch, appUserAccess, storagePaths, vit
              _dtus.push({ _refPath, dataToUpdate })
              console.log('UPDATE MATCHES ONLY')
              database_fns.update_doc(_refPath, dataToUpdate, batch ? null : _cb_local, batch)

              if (useScoringCollection) {
                const _refPath_scores = createRefPath_event(pathViews, ['_results_seasonal', _itemKey, _subCollection_results, levelKey])
                const results_fromMatches = resultsMatches(dataToUpdate)
                if (!_.isEmpty(results_fromMatches)) {
                  _dtus.push({ _refPath_scores, results_fromMatches })
                  database_fns.update_doc(_refPath_scores, results_fromMatches, batch ? null : callback, batch)
                }
              }

            }
          }
          // database_fns.update_doc(_refPath, dataToUpdate, callback)
        }
      } else {
        const _refPath = createRefPath_event(pathViews, [_collectionName])
        const dataToAdd = { parentKeys: pks, [collectionName]: dataToUpdate }
        _dtus.push({ _refPath, dataToAdd })
        // refPath, data, dispatch, batch, appUserAccess, storagePaths, vit
        database_fns.add_doc(_refPath, dataToAdd, batch ? null : callback, batch)
      }
    } else {
      callback({ type: dispatchProps.error, error: 'Bad Pks', dispatch: callback })
    }
  }

}

const resultsMatches = (matches) =>
  _.mapValues(
    _.pickBy(
      matches || {},
      match =>
        match?.results &&
        !_.has(match, '_methodName') &&
        !match?._isDuplicated &&
        !match?._isCancelled &&
        !match?._isRemoved
    ),
    match => ({
      results: match.results
    })
  );

const createMatchesNew = async (database_fns, allowUpdate, pathViews, documentFieldName, matchesToUpdate, collectionName, itemKey, batch) => {

  if (itemKey) {

    convertHelpers.createItemKeys(matchesToUpdate)

    const _levelsToUpdate = _.groupBy(matchesToUpdate, '_matchCollectionKey')

    await Promise.all(
      _.map(_levelsToUpdate, async (_levelMatches, levelKey) => {
        _levelMatches = dataFix.removeEmpties(_levelMatches)
        const _refPathS = createRefPath_event(pathViews, [collectionName, itemKey, 'matches', levelKey])
        await updateLevelMatches(database_fns, _refPathS, documentFieldName, _levelMatches, allowUpdate, true, batch)
      })
    );
  } else {
    console.log('createMatchesNew Error: no itemKey',)
  }
}

const createMatchesFromDot = (dataToUpdate, _subCollection) => {
  const _matches = {}
  Object.keys(dataToUpdate).forEach(k => {
    const _itemKey = k.replace(_subCollection + '.', '')
    _matches[_itemKey] = dataToUpdate[k]
  })
  return _matches
}

const backupSeasonal = async (database_fns, pathViews, dataParents, altSeasonalName, dataRestrictions, dispatch) => {

  const collectionName = altSeasonalName ? altSeasonalName : _seasonalCollectionName

  if (dataRestrictions && (dataRestrictions.all || dataRestrictions.backupSeasonals)) {
    dispatch && dispatch({ type: dispatchProps && dispatchProps.successAlt ? dispatchProps.successAlt : dispatchProps.error })
    return true
  }

  if (dataParents) {
    const parentKeys = {}
    dataParents.forEach(dp => {
      if (pathViews[dp]) {
        parentKeys[dp] = pathViews[dp]
      }
    })

    const sy = getSportsYear()
    parentKeys['sportsYears'] = sy

    getSeasonalForUpdate(database_fns, pathViews, dataParents, collectionName).then(seasonalItem => {
      if (seasonalItem) {
        const si = seasonalItem[Object.keys(seasonalItem)[0]]
        if (si) {
          const _updateRef = createRefPath_event(pathViews, [collectionName + '_backup', si.id])
          database_fns.set_doc(_updateRef, si, true, dispatch)
        }
      }
    })
  }
}

const deleteSeasonalData = async (database_fns, pathViews, dataParents, dataType, data, callback, altData, altSeasonalName) => {

  const collectionName = altSeasonalName ? altSeasonalName : _seasonalCollectionName

  if (dataParents) {
    const parentKeys = {}
    dataParents.forEach(dp => {
      switch (dp) {
        // case 'allSportsYears':
        //   const sy = getSportsYear()
        //   parentKeys['sportsYears'] = sy
        //   break;
        default:
          if (pathViews[dp]) {
            parentKeys[dp] = pathViews[dp]
          }
      }
    })

    const sy = getSportsYear()
    parentKeys['sportsYears'] = sy

    const seasonalData = altData
      ? altData :
      {
        parentKeys,
        [dataType]: data
      }

    getSeasonalForUpdate(database_fns, pathViews, dataParents, collectionName).then(seasonalItem => {
      if (seasonalItem) {
        const si = seasonalItem[Object.keys(seasonalItem)[0]]

        const _updateRef = createRefPath_event(pathViews, [collectionName])
        if (si) {
          if (altData) {
            database_fns.update_doc(_updateRef, altData, callback)
          } else {
            database_fns.set_doc(_updateRef, seasonalData, true, callback)
          }
        } else {
          database_fns.set_doc(_updateRef, seasonalData, false, callback)
        }
      }
    })
  }
}

const updateLevelMatches = async (database_fns, refPath, documentFieldName, matches, allowUpdate, removePrior, batch) => {

  const callback = (r) => {
    console.log('r', r)
  }

  const _matches = {}

  _.forEach(matches, (m, keyOrIndex) => {
    _matches[m._itemKey] = dataFix.removeUnderscoreKeys(m)
  })

  seasonalHelpers.cleanMatchesForUpdate(_matches)

  const dataToUpdate = documentFieldName ? { [documentFieldName]: _matches } : _matches
  const dataToUpdate_new = getMatchFieldData(_matches, documentFieldName)

  console.log('dataToUpdate', dataToUpdate)
  console.log('dataToUpdate_new', dataToUpdate_new)

  if (allowUpdate) {
    if (removePrior) {
      database_fns.delete_doc(refPath).then(
        database_fns.update_doc(refPath, dataToUpdate, batch ? null : callback, batch)
      )
    } else {
      database_fns.update_doc(refPath, dataToUpdate_new, batch ? null : callback, batch)
    }
  } else {
    console.log('dataToUpdate', refPath, dataToUpdate)
  }
}

const getMatchFieldData = (matches, documentFieldName) => _.mapKeys(matches, (value, key) => `${documentFieldName}.${key}`);

// eslint-disable-next-line 
const ammendStartDate = (items) => {
  if (items) {
    Object.keys(items).forEach(key => {
      items[key].key = key
      if (items[key].startDateUTC) { items[key].startDate = formatItem(formatTypes.date, items[key].startDateUTC) }
      if (items[key].startTimeUTC) { items[key].startTime = '7:00 PM' }
    })
  }
}

/**
 * Gets the data based on the refPath_event and the collectionName with dataParents creating wheres
 * @param {string} pathViews 
 * @param {object} dataParents 
 * @param {string} collectionName 
 * @returns 
 */
const getSeasonalForUpdate = async (database_fns, pathViews, dataParents, collectionName) => {

  if (dataParents) {
    const _refPath = createRefPath_event(pathViews, [collectionName])
    const wheres = []

    dataParents.forEach(dp => {
      switch (dp) {
        case 'allSportsYears':
          const sy = getSportsYear()
          wheres.push(where('parentKeys.sportsYears', '==', sy))
          break;
        default:
          if (pathViews[dp]) {
            wheres.push(where('parentKeys.' + dp, '==', pathViews[dp]))
            // firestoreRef = firestoreRef.where('parentKeys.' + dp, '==', pathViews[dp])
          }
      }
    })

    const retData = await database_fns.get_data({ refPath: _refPath, wheres })
    return retData
  } else {
    return null
  }
}

/**
 * 
 * @param {object} parentKeys 
 * @param {array} dataParents 
 * @param {string} collectionName 
 * @returns the data from the collectionName or _seasonalCollectionName via callback
 */
const getSports_collectionItem = async (database_fns, pathViews, parentKeys, collectionName, returnFirstObject, cbProps, callback, collectionKeys) => {

  const cb = (data) => {
    const cbd = {
      collectionName,
      parentKeys,
      data_sportsCollectionItem: data
    }
    callback(cbd, { cbProps })
  }

  const _collectionName = collectionName ? collectionName : _seasonalCollectionName

  const _refPath = collectionKeys ? createRefPath_event(pathViews, [_collectionName, collectionKeys[0], collectionKeys[1]]) : createRefPath_event(pathViews, [_collectionName])

  const wheres = []
  const wherez = []

  Object.keys(parentKeys).forEach(pk => {
    switch (pk) {
      // case 'organzations':
      // case 'districts':
      // case 'sports':
      //   wheres.push(where('parentKeys.' + pk, '==', parseInt(parentKeys[pk])))
      //   break;
      case 'sportsYears':
        wheres.push(where('parentKeys.' + pk, '==', parseInt(parentKeys[pk])))
        wherez.push({ pk, value: parseInt(parentKeys[pk]) })
        break;
      default:
        wheres.push(where('parentKeys.' + pk, '==', parentKeys[pk]))
        wherez.push({ pk, value: parentKeys[pk] })
    }
  })

  if (database_fns) {
    if (callback) {
      database_fns.get_data({ refPath: _refPath, callback: cb, wheres, opts: { returnFirstObject } })
    } else {
      const retData = await database_fns.get_data({ refPath: _refPath, wheres, opts: { returnFirstObject } })
      return retData
    }
  }
}

const updateSportsCollectionItemsWithParentKeys = async (database_fns, seasonalUpdateType, updateProps, collectionName, data) => {
  const { callback } = updateProps
  if (data && Object.keys(data).length > 0) {
    const result = await updateSportsCollectionItemsPromise(database_fns, seasonalUpdateType, updateProps, collectionName, data)
    callback && callback(result)
    return {}
  } else {
    callback && callback({})
    return {}
  }
}

const updateSportsCollectionItemsPromise = async (database_fns, seasonalUpdateType, updateProps, collectionName, data) => {

  const { sportPermissions, pathViews, pks } = updateProps
  const allowUpdate = _allowSportsUpdates.seasonals && sportPermissions?.any && sportPermissions?.[seasonalUpdateType]

  const promises = []
  Object.keys(data).forEach(k => {
    const _dataItem = data[k]
    _dataItem.parentKeys = pks
    delete _dataItem.year
    delete _dataItem.position
    delete _dataItem.uniformNumber
    const refPath = createRefPath_event(pathViews, [collectionName, k])
    if (allowUpdate) {
      promises.push(database_fns.set_doc(refPath, _dataItem, false, null, true))
    } else {
      console.log('refPath', refPath, _dataItem)
    }
  })
  return Promise.all(promises)
}


const filterValidMatches = (dataToUpdate, requiredProps) => {
  return _.pickBy(dataToUpdate, (match) =>
    _.every(requiredProps, (prop) => _.has(match, prop))
  );
};



const getDataToAdd = (pks, collectionName, documentFieldName, _data, fullData, data_new) => {
  let dataToAdd;
  if (fullData) {
    dataToAdd = fullData
  } else if (data_new) {
    if (documentFieldName) {
      dataToAdd = {
        parentKeys: pks,
        [documentFieldName]: data_new
      }
    } else {
      dataToAdd = {
        parentKeys: pks,
        [collectionName]: data_new
      }
    }
  } else {
    if (documentFieldName) {
      dataToAdd = {
        parentKeys: pks,
        [documentFieldName]: _data
      }
    } else {
      dataToAdd = {
        parentKeys: pks,
        ..._data
      }
    }
  }
  dataToAdd = dataFix.removeEmpties(dataToAdd)
  return dataToAdd
}

/**
 * Returns data from `_global.levels`, `_global.sections`, `schools` and `teams` 
 * @param {object} pathViews 
 * @param {function} callback 
 */
const getData_createSeasonInit = (database_fns, pathViews, sportsKey, callback) => {

  const datas = {}

  const dataItems = {
    levels: { isGlobal: true, dataParents: null }, // _globals.levels
    sections: { isGlobal: true, dataParents: null },// _globals.sections
    schools: { isGlobal: false, isSub: true, dataParents: ['organizations', 'districts'] }, // _schools with parentKeys (organizations, districts)
    teams: { isGlobal: false, dataParents: ['organizations', 'districts', 'sports'] }, // teams with parentKeys (organizations, districts, sports)
  }

  const cb_datas = () => {
    let valid = true
    Object.keys(dataItems).forEach(key => {
      if (!datas[key]) {
        valid = false
      }
    })

    if (valid) {
      callback(datas)
    }
  }

  const cb = (data, opts) => {
    const { cbProps, collectionName } = opts ?? {}
    const { collectionName: collectionName_cbProps } = cbProps ?? {}
    const _collectionName = collectionName ? collectionName : collectionName_cbProps
    datas[_collectionName] = data ?? {}
    cb_datas()
  }

  const cb_pk = (data, opts) => {
    const { cbProps, collectionName } = opts ?? {}
    const { collectionName: collectionName_cbProps } = cbProps ?? {}
    const _collectionName = collectionName ? collectionName : collectionName_cbProps
    datas[_collectionName] = data[_collectionName] ?? {}
    cb_datas()
  }

  _.forEach(dataItems, (_dataItem, key) => {
    const { isGlobal, isSub, dataParents } = _dataItem ?? {}
    if (isGlobal) {
      // levels, sections
      database_fns.get_eventData_global(pathViews, key, true, cb, false, { collectionName: key })
    } else {
      if (isSub) {
        // schools 
        const pkdProps = {
          pathViews,
          dataItem: { collectionName: key },
          dataParents,
          callback: cb_pk,
          cbProps: { collectionName: key },
          database_fns,
        }
        getParentKeyData_sports(pkdProps)
      } else {
        // teams
        database_fns.get_data_any({ pathViews, collectionName: key, callback: cb, opts: { sportsKey, dataParents, cbProps: { collectionName: key } } })
      }
    }
  })
}